import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/language-context";
import * as Sentry from "@sentry/react";
import { motion } from "framer-motion";
import { Check, Leaf, Smartphone, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { SiGoogleplay } from "react-icons/si";
import { useLocation } from "wouter";

export default function SuccessPage() {
  const [, navigate] = useLocation();
  const [showContent, setShowContent] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    // Clear localStorage after successful payment
    try {
      localStorage.clear();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { source: "success.localStorage-clear" },
      });
    }

    const timer = setTimeout(() => setShowContent(true), 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center px-6">
      {/* Decorative elements */}
      <div className="absolute top-20 right-20 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-lg w-full">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, type: "spring" }}
          className="text-center mb-8"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-accent flex items-center justify-center">
            <Check className="w-10 h-10 text-accent-foreground" />
          </div>
        </motion.div>

        {showContent && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 text-accent mb-6">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {t.success.welcomeBadge}
                </span>
              </div>

              <h1 className="font-serif text-4xl md:text-5xl font-medium mb-4">
                {t.success.title}
              </h1>

              <p className="text-lg text-muted-foreground max-w-md mx-auto">
                {t.success.subtitle}
              </p>
            </div>

            <div className="bg-card rounded-2xl p-8 shadow-lg mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
                  <Leaf className="w-6 h-6 text-primary-foreground" />
                </div>
                <div>
                  <p className="font-medium">{t.success.premiumTitle}</p>
                  <p className="text-sm text-muted-foreground">
                    {t.success.subscriptionActive}
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-8">
                {t.success.features.map((feature) => (
                  <div
                    key={feature}
                    className="flex items-center gap-3 text-sm"
                  >
                    <Check className="w-4 h-4 text-accent" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <div className="bg-muted/50 rounded-xl p-4 flex items-center gap-4">
                <Smartphone className="w-6 h-6 text-primary" />
                <div>
                  <p className="font-medium text-sm">
                    {t.success.downloadTitle}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t.success.downloadSubtitle}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button
                asChild
                variant="outline"
                size="lg"
                className="flex-1 gap-3"
                data-testid="button-google-play"
              >
                <a
                  href="https://play.google.com/store/apps/details?id=com.evooluir"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <SiGoogleplay className="w-5 h-5" />
                  <div className="text-left">
                    <p className="text-xs text-muted-foreground">
                      {t.success.getItOn}
                    </p>
                    <p className="font-medium">{t.success.googlePlay}</p>
                  </div>
                </a>
              </Button>
            </div>

            {/* <div className="text-center">
              <Button 
                variant="ghost" 
                onClick={() => navigate("/dashboard")}
                className="gap-2"
                data-testid="button-go-dashboard"
              >
                {t.success.goToDashboard}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div> */}
          </motion.div>
        )}
      </div>
    </div>
  );
}
